import { z } from 'zod';
import { CurrencyCode } from './money.ts';

/**
 * UrbanRats → operator seamless-wallet callbacks, v1 (docs §10). UrbanRats is the caller; the
 * operator's wallet is the authority for the player's balance. Proposed UrbanRats schema, not a copy
 * of any vendor API. All requests and responses are signed (see packages/domain/src/walletSigning.ts).
 *
 * Idempotency (economic identity, never the transport request):
 *   /bet, /win:  tenant + operation + transactionId
 *   /rollback:   tenant + originalTransactionId + reversalType
 * Same key + different economic payload → REJECTED with code IDEMPOTENCY_CONFLICT.
 * Retries reuse the economic id with a new requestId/nonce.
 */
export const WIRE_PROTOCOL_VERSION = 1;

/** Amount in the partner's wire scale: amount = integer atomic units at `scale` (decimal string). */
export const WireMoney = z.object({
  amount: z.string().regex(/^(0|[1-9][0-9]*)$/).max(40),
  currency: CurrencyCode,
  scale: z.number().int().min(0).max(18),
});
export type WireMoney = z.infer<typeof WireMoney>;

const Base = {
  protocolVersion: z.literal(WIRE_PROTOCOL_VERSION),
  /** Transport id: new on every attempt. */
  requestId: z.string().min(8).max(128),
  /** Operator's player id (the only player identifier sent; no PII). */
  playerId: z.string().min(1).max(128),
  gameId: z.string().min(1).max(64),
  /** The operator's wallet-session token from the launch, when one was given (absent otherwise). */
  operatorToken: z.string().min(1).max(512).optional(),
};

export const WalletBalanceRequest = z.object({ ...Base, currency: CurrencyCode }).strict();
export const WalletBetRequest = z
  .object({
    ...Base,
    transactionId: z.string().min(1).max(128),
    betId: z.string().min(1).max(128),
    gameRoundId: z.string().min(1).max(128),
    /** Partner's wallet round (defined per partner contract; v1: one wallet round per bet). */
    walletRoundId: z.string().min(1).max(160),
    amount: WireMoney,
  })
  .strict();
export const WalletWinRequest = z
  .object({
    ...Base,
    transactionId: z.string().min(1).max(128),
    betId: z.string().min(1).max(128),
    gameRoundId: z.string().min(1).max(128),
    walletRoundId: z.string().min(1).max(160),
    /** The debit this result belongs to; null for a freebet bet (no debit — see funding). */
    debitTransactionId: z.string().min(1).max(128).nullable(),
    /** FREEBET: the stake was a freebet (no /bet was sent); credit the amount, the round closes. */
    funding: z.enum(['CASH', 'FREEBET']).optional(),
    promotionId: z.string().min(1).max(128).optional(),
    /** Zero for a loss: a zero-amount win closes the wallet round. */
    amount: WireMoney,
    roundClosed: z.literal(true),
  })
  .strict();
export const ReversalType = z.enum(['BET_REJECTED', 'ROUND_VOID', 'LATE_DEBIT_COMPENSATION']);
export type ReversalType = z.infer<typeof ReversalType>;
export const WalletRollbackRequest = z
  .object({
    ...Base,
    transactionId: z.string().min(1).max(128),
    originalTransactionId: z.string().min(1).max(128),
    reversalType: ReversalType,
    betId: z.string().min(1).max(128),
    gameRoundId: z.string().min(1).max(128),
    walletRoundId: z.string().min(1).max(160),
    amount: WireMoney,
  })
  .strict();
/** Optional capability: status of an economic transaction (for reconciliation after a lost response). */
export const WalletStatusRequest = z.object({ protocolVersion: z.literal(WIRE_PROTOCOL_VERSION), requestId: z.string().min(8).max(128), transactionId: z.string().min(1).max(128) }).strict();

export const WalletErrorCode = z.enum(['INSUFFICIENT_FUNDS', 'SESSION_EXPIRED', 'UNKNOWN_PLAYER', 'IDEMPOTENCY_CONFLICT', 'DEBIT_CANCELLED', 'ROUND_CLOSED', 'CURRENCY_MISMATCH', 'INVALID_SIGNATURE', 'REPLAYED_REQUEST', 'LIMIT_EXCEEDED', 'INTERNAL']);
export type WalletErrorCode = z.infer<typeof WalletErrorCode>;

/**
 * `transaction` describes the economic operation (first outcome, stable across retries); `balance`
 * is the player's balance at response time. They are different facts and never merged.
 */
export const WalletResponse = z.object({
  protocolVersion: z.literal(WIRE_PROTOCOL_VERSION),
  requestId: z.string(),
  status: z.enum(['OK', 'REJECTED']),
  code: WalletErrorCode.nullable(),
  transaction: z
    .object({
      transactionId: z.string(),
      partnerTransactionId: z.string().nullable(),
      /** true only on the attempt that changed the balance; false for a duplicate. */
      applied: z.boolean(),
      alreadyProcessed: z.boolean(),
      /** Rollback only: whether the original debit existed (false → tombstone recorded). */
      originalFound: z.boolean().nullable(),
    })
    .nullable(),
  balance: WireMoney.nullable(),
  balanceVersion: z.string().max(64).nullable(),
});
export type WalletResponse = z.infer<typeof WalletResponse>;

export const WalletStatusResponse = z.object({
  protocolVersion: z.literal(WIRE_PROTOCOL_VERSION),
  requestId: z.string(),
  transactionId: z.string(),
  state: z.enum(['APPLIED', 'REJECTED', 'REVERSED', 'NOT_FOUND']),
  code: WalletErrorCode.nullable(),
});

export const WALLET_CALLBACKS = [
  { path: '/balance', summary: 'Current balance (read-only)', request: WalletBalanceRequest, response: WalletResponse },
  { path: '/bet', summary: 'Debit a stake (idempotent: tenant + BET + transactionId)', request: WalletBetRequest, response: WalletResponse },
  { path: '/win', summary: 'Credit a result; amount 0 for a loss (idempotent: tenant + WIN + transactionId). No normal rollback after success.', request: WalletWinRequest, response: WalletResponse },
  { path: '/rollback', summary: 'Reverse a debit (idempotent: tenant + originalTransactionId + reversalType). Arriving before the debit leaves a tombstone.', request: WalletRollbackRequest, response: WalletResponse },
  { path: '/status', summary: 'OPTIONAL capability: state of an economic transaction', request: WalletStatusRequest, response: WalletStatusResponse },
] as const;

export const WALLET_SIGNATURE_HEADERS = {
  keyId: 'x-ur-key-id',
  timestamp: 'x-ur-timestamp',
  nonce: 'x-ur-nonce',
  signature: 'x-ur-signature',
} as const;
